import { useState, useEffect } from 'react';
import { resetTracking } from '@/services/tracking';
import { useNavigate } from 'react-router-dom';
import {
  useAdminUsers,
  useDeleteUser,
  useChangeUserRole,
  useStartImpersonation,
  useCreateUser,
  useAdminTeams,
  useAdminAgencies,
} from '@/hooks/useAdmin';
import { AdminUserListFilters, AdminUserListItem, CreateUserRequest, CreateUserResponse } from '@/types/admin';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { Combobox } from '@/components/ui/Combobox';
import { Modal } from '@/components/ui/Modal';
import { ModalFooter } from '@/components/ui/ModalFooter';
import { ConfirmDialog, PromptDialog, Card, Badge, EmptyState, Pagination } from '@/components/ui';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { ActionButton } from '@/components/admin/ActionButton';
import { AdminPageHeader } from '@/components/admin/AdminPageHeader';
import { useTimezone } from '@/contexts/TimezoneContext';
import { useAuth } from '@/hooks/useAuth';
import { setAccessToken, formatApiError } from '@/services/api';
import { toast } from '@/utils/toast';
import { ICONS } from '@/constants/icons';
import { useDebounce } from '@/hooks/useDebounce';
import { isLastReviewerCancel } from '@/stores/lastReviewerConfirm.store';

/**
 * Admin Users Page
 * List and manage all users
 */

const ROLES = ['USER', 'ADMIN', 'AGENCY_ADMIN', 'SUPER_ADMIN'] as const;

export const AdminUsers = () => {
  const navigate = useNavigate();
  const { formatDateTime } = useTimezone();
  const { refreshUser } = useAuth();
  const [filters, setFilters] = useState<AdminUserListFilters>({
    page: 1,
    limit: 20,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  useEffect(() => {
    setFilters((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
  }, [debouncedSearch]);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; user: AdminUserListItem | null }>({
    open: false,
    user: null,
  });
  const [impersonateDialog, setImpersonateDialog] = useState<{
    open: boolean;
    user: AdminUserListItem | null;
  }>({
    open: false,
    user: null,
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createdResetUrl, setCreatedResetUrl] = useState<string | null>(null);

  const { data, isLoading, error } = useAdminUsers({ ...filters, search: debouncedSearch || undefined });
  const deleteUser = useDeleteUser();
  const changeRole = useChangeUserRole();
  const startImpersonation = useStartImpersonation();
  const createUser = useCreateUser();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
  };

  const handleDelete = async (user: AdminUserListItem) => {
    setDeleteDialog({ open: true, user });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.user) return;

    try {
      await deleteUser.mutateAsync(deleteDialog.user.id);
      toast.success(`User ${deleteDialog.user.email} deleted`);
      setDeleteDialog({ open: false, user: null });
    } catch (err) {
      if (isLastReviewerCancel(err)) return;
      toast.error('Failed to delete user');
      setDeleteDialog({ open: false, user: null });
    }
  };

  const handleRoleChange = async (user: AdminUserListItem, newRole: typeof ROLES[number]) => {
    try {
      await changeRole.mutateAsync({ id: user.id, role: newRole });
      toast.success(`Role updated to ${newRole}`);
    } catch (err) {
      toast.error('Failed to update role');
    }
  };

  const handleImpersonate = async (user: AdminUserListItem) => {
    if (user.isSuperAdmin) {
      toast.error('Cannot impersonate super admins');
      return;
    }

    setImpersonateDialog({ open: true, user });
  };

  const handleImpersonateConfirm = async (reason: string) => {
    if (!impersonateDialog.user || !reason.trim()) return;

    try {
      const result = await startImpersonation.mutateAsync({
        targetUserId: impersonateDialog.user.id,
        reason,
        isReadOnly: false,
      });

      // Set the impersonation access token in memory (like God Mode does)
      await resetTracking();
      setAccessToken(result.token);

      // Refresh user context to pick up impersonation session
      await refreshUser();

      toast.success(`Now impersonating ${impersonateDialog.user.email}`);
      setImpersonateDialog({ open: false, user: null });

      // Navigate to target user's dashboard (SPA navigation preserves token in memory)
      navigate('/');
    } catch (err) {
      toast.error('Failed to start impersonation');
      setImpersonateDialog({ open: false, user: null });
    }
  };

  const handlePageChange = (newPage: number) => {
    setFilters({ ...filters, page: newPage });
  };

  if (error) {
    return (
      <div className="bg-red-50 text-red-600 p-4 rounded-lg">
        Error loading users: {formatApiError(error).message}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Users"
        subtitle="Manage all platform users"
        action={<Button onClick={() => setShowCreateModal(true)}>Create User</Button>}
      />

      {/* Search & Filters */}
      <Card padding="sm">
        <form onSubmit={handleSearch} className="flex gap-4 flex-wrap">
          <Input
            type="text"
            placeholder="Search by email or name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 min-w-[200px]"
            inputSize="sm"
          />
          <Select
            value={filters.role || ''}
            onValueChange={(value) => setFilters({ ...filters, role: value as AdminUserListFilters['role'] || undefined, page: 1 })}
            options={ROLES.map(role => ({ value: role, label: role }))}
            placeholder="All Roles"
            clearable
            clearLabel="All Roles"
            size="sm"
          />
          <Select
            value={filters.isActive?.toString() || ''}
            onValueChange={(value) => setFilters({ ...filters, isActive: value === '' ? undefined : value === 'true', page: 1 })}
            options={[
              { value: 'true', label: 'Active' },
              { value: 'false', label: 'Inactive' }
            ]}
            placeholder="All Status"
            clearable
            clearLabel="All Status"
            size="sm"
          />
        </form>
      </Card>

      {/* Users Table */}
      <Card padding="none" className="overflow-hidden">
        {isLoading ? (
          <div className="p-8 flex justify-center">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">User</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Role</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Team / Brand Portfolio</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Status</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Created</th>
                    <th className="text-right px-4 py-3 text-sm font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {(data?.data ?? []).map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div>
                            <div className="font-medium text-gray-900">{user.fullName || 'No name'}</div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                          {user.agencyStaff && user.agencyStaff.length > 0 && (
                            <Badge variant="primary" className="whitespace-nowrap">
                              Portfolio Staff
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Select
                            value={user.role}
                            onValueChange={(value) => handleRoleChange(user, value as typeof ROLES[number])}
                            options={ROLES.map(role => ({ value: role, label: role }))}
                            disabled={changeRole.isPending}
                            size="sm"
                            className="min-w-[140px]"
                          />
                          {user.isSuperAdmin && (
                            <Badge variant="primary" className="whitespace-nowrap">
                              Super
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {user.teamName ? (
                          <a
                            href={`/admin/teams/${user.teamId}`}
                            className="text-primary-600 hover:text-primary-800 hover:underline"
                          >
                            {user.teamName}
                          </a>
                        ) : user.agencyStaff && user.agencyStaff.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {user.agencyStaff.map((staff) => (
                              <a
                                key={staff.agencyId}
                                href={`/admin/agencies/${staff.agencyId}`}
                                className="px-2 py-0.5 bg-primary-50 text-primary-800 text-xs rounded-full hover:bg-primary-100"
                              >
                                {staff.agencyName}
                              </a>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={user.isActive ? 'success' : 'danger'}>
                          {user.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {formatDateTime(user.createdAt)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <ActionButton
                            icon={ICONS.EYE}
                            tooltip="View Details"
                            href={`/admin/users/${user.id}`}
                          />
                          {!user.isSuperAdmin && (
                            <ActionButton
                              icon="switcher"
                              tooltip="Impersonate User"
                              onClick={() => handleImpersonate(user)}
                              variant="warning"
                              disabled={startImpersonation.isPending}
                            />
                          )}
                          <ActionButton
                            icon={ICONS.DELETE}
                            tooltip="Delete User"
                            onClick={() => handleDelete(user)}
                            variant="danger"
                            disabled={deleteUser.isPending}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                  {(data?.data?.length ?? 0) === 0 && (
                    <tr>
                      <td colSpan={6}>
                        <EmptyState
                          icon={ICONS.USERS}
                          title="No users found"
                          description="Try adjusting your search or filters."
                        />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data?.meta && data.meta.totalPages > 1 && (
              <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  Showing {((data.meta.page - 1) * data.meta.limit) + 1} to {Math.min(data.meta.page * data.meta.limit, data.meta.total)} of {data.meta.total}
                </div>
                <Pagination
                  page={data.meta.page}
                  totalPages={data.meta.totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        )}
      </Card>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => !open && setDeleteDialog({ open: false, user: null })}
        title="Delete User"
        description={`Are you sure you want to delete ${deleteDialog.user?.email}? This action cannot be undone.`}
        confirmText="Delete User"
        cancelText="Cancel"
        variant="danger"
        onConfirm={handleDeleteConfirm}
        loading={deleteUser.isPending}
      />

      {/* Impersonation Dialog */}
      <PromptDialog
        open={impersonateDialog.open}
        onOpenChange={(open) => !open && setImpersonateDialog({ open: false, user: null })}
        title="Impersonate User"
        description={`You are about to impersonate "${impersonateDialog.user?.email}". This will be logged for audit purposes.`}
        label="Reason for impersonation"
        placeholder="e.g., Investigating reported bug, Testing user-specific issue"
        inputType="textarea"
        required
        confirmText="Start Impersonation"
        cancelText="Cancel"
        variant="info"
        onConfirm={handleImpersonateConfirm}
        loading={startImpersonation.isPending}
      />

      {/* Create User Modal */}
      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          createUser={createUser}
          onResetUrl={(url) => setCreatedResetUrl(url)}
        />
      )}

      {/* Reset URL Dialog after user creation */}
      {createdResetUrl && (
        <CreatedResetUrlDialog
          resetUrl={createdResetUrl}
          onClose={() => setCreatedResetUrl(null)}
        />
      )}
    </div>
  );
};

// ============================================================================
// CREATE USER MODAL
// ============================================================================

const USER_ROLES = ['USER', 'ADMIN', 'AGENCY_ADMIN', 'SUPER_ADMIN'] as const;
const TEAM_ROLES = ['OWNER', 'ADMIN', 'MEMBER', 'REVIEWER'] as const;
const AGENCY_ROLES = ['OWNER', 'ADMIN', 'MANAGER', 'MEMBER'] as const;

interface CreateUserModalProps {
  onClose: () => void;
  createUser: ReturnType<typeof useCreateUser>;
  onResetUrl: (url: string) => void;
}

const CreateUserModal = ({ onClose, createUser, onResetUrl }: CreateUserModalProps) => {
  const [formData, setFormData] = useState<CreateUserRequest>({
    email: '',
    fullName: '',
    role: 'USER',
    isSuperAdmin: false,
    timezone: 'Europe/Berlin',
    teamRole: 'MEMBER',
    agencyRole: 'MEMBER',
    sendWelcomeEmail: true,
  });
  // Team picker: two-state pattern — teamId = resolved form value, teamInputValue = Combobox display + search
  const [teamId, setTeamId] = useState('');
  const [teamInputValue, setTeamInputValue] = useState('');
  const teamSearchQuery = useDebounce(teamInputValue, 300);
  const { data: teamsData, isLoading: isLoadingTeams } = useAdminTeams({
    search: teamSearchQuery || undefined,
    limit: 50,
    sortBy: 'name',
    sortOrder: 'asc',
  });

  // Agency picker: same two-state pattern
  const [agencyId, setAgencyId] = useState('');
  const [agencyInputValue, setAgencyInputValue] = useState('');
  const agencySearchQuery = useDebounce(agencyInputValue, 300);
  const { data: agenciesData, isLoading: isLoadingAgencies } = useAdminAgencies({
    search: agencySearchQuery || undefined,
    limit: 50,
    sortBy: 'name',
    sortOrder: 'asc',
  });

  const [errors, setErrors] = useState<Partial<Record<keyof CreateUserRequest, string>>>({});

  const teamOptions = [
    { value: '', label: '(no team)' },
    ...(teamsData?.data ?? []).map((t) => ({ value: String(t.id), label: t.name })),
  ];

  const agencyOptions = [
    { value: '', label: '(no brand portfolio)' },
    ...(agenciesData?.data ?? []).map((a) => ({ value: String(a.id), label: a.name })),
  ];

  const handleTeamComboboxChange = (val: string) => {
    if (val !== '' && !isNaN(parseInt(val, 10))) {
      // User selected an option — show its name
      setTeamId(val);
      const picked = teamOptions.find((t) => t.value === val);
      setTeamInputValue(picked?.label ?? val);
    } else {
      // User is typing or cleared the field
      setTeamInputValue(val);
      setTeamId('');
    }
  };

  const handleAgencyComboboxChange = (val: string) => {
    if (val !== '' && !isNaN(parseInt(val, 10))) {
      setAgencyId(val);
      const picked = agencyOptions.find((a) => a.value === val);
      setAgencyInputValue(picked?.label ?? val);
    } else {
      setAgencyInputValue(val);
      setAgencyId('');
    }
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof CreateUserRequest, string>> = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Enter a valid email address';
    }

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRoleChange = (role: string) => {
    const typedRole = role as CreateUserRequest['role'];
    setFormData((prev) => ({
      ...prev,
      role: typedRole,
      isSuperAdmin: typedRole === 'SUPER_ADMIN' ? true : prev.isSuperAdmin,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const payload: CreateUserRequest = {
      ...formData,
      teamId: teamId ? parseInt(teamId, 10) : undefined,
      agencyId: agencyId ? parseInt(agencyId, 10) : undefined,
    };
    if (!payload.teamId) {
      delete payload.teamRole;
    }
    if (!payload.agencyId) {
      delete payload.agencyRole;
    }

    try {
      const result = (await createUser.mutateAsync(payload)) as CreateUserResponse;
      toast.success(`User ${result.user.email} created`);
      onClose();
      if (result.resetUrl) {
        onResetUrl(result.resetUrl);
      }
    } catch (err) {
      toast.error('Failed to create user');
    }
  };

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title="Create New User"
      size="md"
      footer={
        <ModalFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={createUser.isPending}
          >
            Cancel
          </Button>
          <Button type="submit" form="create-user-form" loading={createUser.isPending}>
            Create User
          </Button>
        </ModalFooter>
      }
    >
      <form id="create-user-form" onSubmit={handleSubmit} className="space-y-4">
        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email *
          </label>
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="user@example.com"
            required
          />
          {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
        </div>

        {/* Full Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Full Name *
          </label>
          <Input
            type="text"
            value={formData.fullName}
            onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
            placeholder="Jane Doe"
            required
          />
          {errors.fullName && <p className="text-xs text-red-600 mt-1">{errors.fullName}</p>}
        </div>

        {/* Role */}
        <div>
          <Select
            label="Role"
            value={formData.role ?? 'USER'}
            onValueChange={handleRoleChange}
            options={USER_ROLES.map((r) => ({ value: r, label: r }))}
          />
        </div>

        {/* Super Admin */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="create-super-admin"
            checked={formData.isSuperAdmin ?? false}
            onChange={(e) => setFormData({ ...formData, isSuperAdmin: e.target.checked })}
            className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:outline-none focus:ring-0"
          />
          <label htmlFor="create-super-admin" className="text-sm font-medium text-gray-700">
            Super Admin (full system access)
          </label>
        </div>

        {/* Timezone */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Timezone
          </label>
          <Input
            type="text"
            value={formData.timezone ?? ''}
            onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
            placeholder="Europe/Berlin"
          />
        </div>

        {/* Assign to Team */}
        <div className="border border-gray-200 rounded-lg p-4 space-y-3">
          <h3 className="text-sm font-semibold text-gray-700">Assign to Team (optional)</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Team</label>
            <Combobox
              value={teamInputValue}
              onChange={handleTeamComboboxChange}
              options={teamOptions}
              placeholder="Search teams…"
              loading={isLoadingTeams}
              emptyMessage="No teams found"
            />
          </div>
          {teamId && (
            <div>
              <Select
                label="Team Role"
                value={formData.teamRole ?? 'MEMBER'}
                onValueChange={(v) => setFormData({ ...formData, teamRole: v as CreateUserRequest['teamRole'] })}
                options={TEAM_ROLES.map((r) => ({ value: r, label: r }))}
              />
            </div>
          )}
        </div>

        {/* Make Agency Staff */}
        <div className="border border-gray-200 rounded-lg p-4 space-y-3">
          <h3 className="text-sm font-semibold text-gray-700">Make Portfolio Staff (optional)</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Brand Portfolio</label>
            <Combobox
              value={agencyInputValue}
              onChange={handleAgencyComboboxChange}
              options={agencyOptions}
              placeholder="Search brand portfolios…"
              loading={isLoadingAgencies}
              emptyMessage="No brand portfolios found"
            />
          </div>
          {agencyId && (
            <div>
              <Select
                label="Portfolio Role"
                value={formData.agencyRole ?? 'MEMBER'}
                onValueChange={(v) => setFormData({ ...formData, agencyRole: v as CreateUserRequest['agencyRole'] })}
                options={AGENCY_ROLES.map((r) => ({ value: r, label: r }))}
              />
            </div>
          )}
        </div>

        {/* Send Welcome Email */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="create-send-email"
            checked={formData.sendWelcomeEmail ?? true}
            onChange={(e) => setFormData({ ...formData, sendWelcomeEmail: e.target.checked })}
            className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:outline-none focus:ring-0"
          />
          <label htmlFor="create-send-email" className="text-sm font-medium text-gray-700">
            Send welcome / password-setup email
          </label>
        </div>
      </form>
    </Modal>
  );
};

// ============================================================================
// CREATED RESET URL DIALOG
// ============================================================================

interface CreatedResetUrlDialogProps {
  resetUrl: string;
  onClose: () => void;
}

const CreatedResetUrlDialog = ({ resetUrl, onClose }: CreatedResetUrlDialogProps) => {
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(resetUrl);
      toast.success('Setup link copied to clipboard');
    } catch {
      toast.error('Failed to copy link');
    }
  };

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title="User Created"
      size="sm"
      footer={
        <ModalFooter>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          The user was created. If the email did not arrive, share this password setup link
          manually:
        </p>
        <div className="flex gap-2">
          <Input
            type="text"
            readOnly
            value={resetUrl}
            aria-label="Password setup link"
            className="flex-1 text-xs font-mono bg-gray-50 truncate"
            onFocus={(e) => e.target.select()}
          />
          <Button variant="secondary" size="sm" onClick={handleCopy}>
            Copy
          </Button>
        </div>
        <p className="text-xs text-amber-600">This link expires in 7 days.</p>
      </div>
    </Modal>
  );
};

export default AdminUsers;
